import { InfoCircleOutlined } from "@ant-design/icons"
import { Button, Flex, Popover, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../constants/routes.ts"
import { getGuideContextualHelp } from "../content/contextual-help.ts"
import { useGuideTour } from "../tour/GuideTourContext.tsx"
import { useProductTour } from "../tour/ProductTourContext.tsx"

const { Title, Text } = Typography

interface GuideHelpTriggerProps {
  helpId: string
  className?: string
}

/**
 * A small "ⓘ" affordance that explains one concept in place — opening it
 * never leaves the current screen (Take a tour / Learn more still do, by
 * design, but the explanation itself doesn't require it).
 */
export const GuideHelpTrigger = ({
  helpId,
  className
}: GuideHelpTriggerProps) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { startTour } = useGuideTour()
  const { isActive: productTourActive, skip: stopProductTour } =
    useProductTour()
  const entry = getGuideContextualHelp(helpId)

  // The two tour engines don't guard against each other — starting this one
  // while the Product Tour is mid-walkthrough would mount both overlays at
  // once (same fixed z-index, two sets of Next/Back/Skip controls).
  const handleStartTour = () => {
    if (productTourActive) stopProductTour()
    startTour(entry!.relatedTourId!)
  }

  if (!entry) return null

  return (
    <Popover
      trigger="click"
      placement="bottomLeft"
      content={
        <Flex vertical gap={8} className="max-w-72">
          <Title level={5} className="!mb-0">
            {t(entry.titleKey)}
          </Title>
          <Text>{t(entry.bodyKey)}</Text>
          <Flex gap={8} className="mt-1">
            {entry.relatedTourId && (
              <Button size="small" onClick={handleStartTour}>
                {t("guide.contextualHelp.common.takeATour")}
              </Button>
            )}
            {entry.relatedLessonId && (
              <Button
                size="small"
                type="link"
                onClick={() =>
                  navigate(RoutesPaths.guide.lesson(entry.relatedLessonId!))
                }
              >
                {t("guide.contextualHelp.common.learnMore")}
              </Button>
            )}
          </Flex>
        </Flex>
      }
    >
      <Button
        type="text"
        size="small"
        className={className}
        icon={<InfoCircleOutlined />}
        aria-label={t(entry.titleKey)}
      >
        {t(entry.titleKey)}
      </Button>
    </Popover>
  )
}
